import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Tables } from "@repo/utils";
import { STICKY_LEFT_CLASS, STICKY_RIGHT_CLASS } from "@utils";

export const NavigationDataCode = "nav"
export const NavigationDataKey = `data-${NavigationDataCode}`

export const useNavigationKeybinds = (currentEstimateStructure) => {
  
  const lastActiveElement = useRef(null);
  const lastActiveElementRow = useRef(null);
  
  const focusListener = useCallback(() => {
    if(window?.document.activeElement.dataset?.[NavigationDataCode]) {
      lastActiveElement.current = window?.document.activeElement.dataset[NavigationDataCode];
      lastActiveElementRow.current = window?.document.activeElement.closest('[role=row]');
    }
  }, []);
  
  useLayoutEffect(() => {
    const element = window.document.querySelector(`[data-t=${Tables.BREAKDOWN}] input[${NavigationDataKey}], [data-t=${Tables.BREAKDOWN}] a[${NavigationDataKey}]`);
    if(!element)
      return;
    lastActiveElementRow.current = element.closest('[role=row]');
    lastActiveElement.current = element.dataset[NavigationDataCode];
  }, []);
  
  const handleArrowNavigation = useCallback((e) => {
    if(
      !e.key
      || document.activeElement.nodeName === "TEXTAREA"
      || document.activeElement.className.includes("ql-editor")
    )
      return;
    
    const key = e.key.toUpperCase();
    let target = e.target;
    
    if(!Object.values(NavKeys).includes(key))
      return;
    
    let rowElement = lastActiveElementRow.current;
    
    if(target.nodeName !== "BODY")
      rowElement = target.closest('[role=row]');
    else if(lastActiveElement.current)
      target = rowElement.querySelector(`[${NavigationDataKey}=${lastActiveElement.current}]`);
    
    /** @note: the opposite should be true for "enter" key handling */
    if(!target.dataset?.[NavigationDataCode])
      return;
    
    // const inputsSelector = `input[${NavigationDataKey}], .ql-editor[${NavigationDataKey}], a[${NavigationDataKey}]`;
    const inputsSelector = `input[${NavigationDataKey}], a[${NavigationDataKey}]`;
    
    // @note: maybe WTs and other columns should have separate indexing?
    // let rowElement = target.closest('[role=row]') || lastActiveElementRow.current;
    let cellType = target.dataset[NavigationDataCode];
    
    const currentRows = window.document.querySelectorAll(`[role=row]:has(${inputsSelector})`);
    const currentInputRows = window.document.querySelectorAll(`[role=row]:has([${NavigationDataKey}=${cellType}])`);
    let currentRowColumns = rowElement.querySelectorAll(inputsSelector);
    
    const startingRowIndex = Array.prototype.indexOf.call(currentRows, rowElement);
    const startingInputRowIndex = Array.prototype.indexOf.call(currentInputRows, rowElement);
    const startingColumnIndex = Array.prototype.indexOf.call(currentRowColumns, target);
    
    const maxRowIndex = currentRows.length - 1;
    // const maxInputRowIndex = currentInputRows.length - 1;
    const maxColumnIndex = currentRowColumns.length - 1;
    
    let currentRowIndex = startingRowIndex;
    let currentInputRowIndex = startingInputRowIndex;
    let currentColumnIndex = startingColumnIndex;
    
    switch ( key ) {
      case NavKeys.left:
        currentColumnIndex = Math.max(0, currentColumnIndex - 1);
        break;
      case NavKeys.right:
        currentColumnIndex = Math.min(maxColumnIndex, currentColumnIndex + 1);
        break;
      case NavKeys.up:
        currentRowIndex = Math.max(0, currentRowIndex - 1);
        currentInputRowIndex = currentInputRowIndex - 1;
        break;
      case NavKeys.down:
        currentRowIndex = Math.min(maxRowIndex, currentRowIndex + 1);
        currentInputRowIndex = currentInputRowIndex + 1;
        break;
    }
    
    if(currentColumnIndex === startingColumnIndex && currentRowIndex === startingRowIndex)
      return;
    
    let rowTypeChanged = false;
    
    if(currentRowIndex !== startingRowIndex) {
      rowElement = currentInputRows[currentInputRowIndex];
      if(!rowElement) {
        rowElement = currentRows[currentRowIndex];
        currentColumnIndex = 0;
        rowTypeChanged = true;
      }
      
      currentRowColumns = rowElement.querySelectorAll(inputsSelector);
    }
    
    if(currentColumnIndex !== startingColumnIndex || rowTypeChanged) {
      cellType = currentRowColumns[currentColumnIndex].dataset[NavigationDataCode]
    }
    
    
    const element = rowElement.querySelector(`[${NavigationDataKey}=${cellType}]`);
    
    if(!element || element === target)
      return;
    
    e.preventDefault();
    
    const container = element.closest("div[role='row']");
    const sectionContainer = container.closest("div.sectionContainer");
    const r = container.getBoundingClientRect();
    const pinnedLeft = container.querySelectorAll(`div.${STICKY_LEFT_CLASS}`);
    const pinnedRight = container.querySelectorAll(`div.${STICKY_RIGHT_CLASS}`);
    const boundaryLeft = pinnedLeft[pinnedLeft.length - 1]?.getBoundingClientRect().right || r.left;
    const boundaryRight = pinnedRight[pinnedRight.length - 1]?.getBoundingClientRect().left || r.right;
    
    
    element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const elementPos = element.getBoundingClientRect();
    
    if(boundaryLeft > elementPos.left) {
      sectionContainer.scrollTo({
        behavior: "smooth",
        left: sectionContainer.scrollLeft - (boundaryLeft - elementPos.left)
      });
    } else if(boundaryRight < elementPos.right) {
      sectionContainer.scrollTo({
        behavior: "smooth",
        left: sectionContainer.scrollLeft + (elementPos.right - boundaryRight)
      });
    }
    
    // const r = container.getBoundingClientRect();
    // if(r.y < 0 || r.y + r.height > stateStore.scrollRef.offsetHeight) {
      // const top = r.y < 0
      //   ? container.offsetTop + 24
      //   : stateStore.scrollRef.offsetHeight - container.offsetTop - 4;
      //
      // stateStore.scrollRef?.scrollTo({ top, behavior: "smooth" });
    // element.scrollIntoView({ block: "center", behavior: "smooth" });
    // }
    // if(element.className.includes("ql-editor"))
    //   element.click();
    // else
    element.focus({ preventScroll: true });
  }, []);
  
  const handleNewRowCreation = useCallback((e) => {
    if(
      !e.key
      || e.key.toUpperCase() !== "ENTER"
      || e.target.nodeName === "INPUT"
      || e.target.nodeName === "TEXTAREA"
      || e.target.dataset?.[NavigationDataCode]
      || e.target.className.includes("ql-editor")
      || !currentEstimateStructure
      || !currentEstimateStructure?.lastOpenedElement
    )
      return;
    
    if(document.activeElement.nodeName === "A" || document.activeElement.nodeName === "BUTTON")
      document.activeElement.blur();
    
    const parent = currentEstimateStructure.lastOpenedElement.split("/").slice(1).reduce((element, key) => element[key], currentEstimateStructure);
    if(!parent)
      return;
    
    currentEstimateStructure.historyManager.startGroup();
    /** @note: 09.07.26 - temporarily switched to old flow */
    // const newElementPath = parent.addChild(true, !parent.hasChildren);
    // currentEstimateStructure.updateLastAddedElement(newElementPath);
    parent.addChild(true, !parent.hasChildren);
    currentEstimateStructure.historyManager.stopGroup();
    
    // const hashedPath = [Tables.BREAKDOWN, ...parent.treePath].map((id) => hashCode(id)).join("/")
    // const element = document.querySelector(`[data-p="${hashedPath}"]`);
    // if(!element)
    //   return;
    
    /** @note: autofocus should do the scrolling for us */
    // element?.scrollIntoView({block: "center"});
    // const top = element.offsetTop + element.offsetHeight + document.body.offsetHeight/2;
    // if(top > 0)
    //   stateStore.scrollRef?.scrollTo({ top });
      // stateStore.scrollRef?.scrollTo({ top, behavior: "smooth" });
    
    // element.lastChild?.scrollIntoView({block: "center" })
  }, [currentEstimateStructure]);
  
  useEffect(() => {
    window.document.addEventListener("keydown", handleArrowNavigation);
    window.document.addEventListener("keydown", handleNewRowCreation);
    window.document.addEventListener('focus', focusListener, true);
    
    return () => {
      window.document.removeEventListener("keydown", handleArrowNavigation);
      window.document.removeEventListener("keydown", handleNewRowCreation);
      window.document.addEventListener('focus', focusListener, true);
    }
  }, []);
}

const NavKeys = {
  right: "ARROWRIGHT",
  left: "ARROWLEFT",
  down: "ARROWDOWN",
  up: "ARROWUP"
}